const PHOTO_BASE_URL = process.env.REACT_APP_PHOTO_URL || "";
const APP_MEDIA_PREFIX = "/media/uploads";
const LEGACY_APP_MEDIA_PREFIX = "/inventory/uploads";

export const resolveMediaUrl = (value) => {
  if (!value || typeof value !== "string") return "";

  const trimmed = value.trim();
  if (!trimmed) return "";

  if (
    trimmed.startsWith("data:") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("blob:")
  ) {
    return trimmed;
  }

  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${PHOTO_BASE_URL}${path}`;
};

/**
 * Normalize any media URL/path to `/uploads/filename...` (no origin).
 */
export const toUploadRelativePath = (value) => {
  if (!value || typeof value !== "string") return "";

  let trimmed = value.trim();
  if (!trimmed) return "";

  try {
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      const parsed = new URL(trimmed);
      trimmed = parsed.pathname || "";
    }
  } catch {
    // keep trimmed
  }

  trimmed = trimmed.split("?")[0].split("#")[0].replace(/\\/g, "/");
  if (!trimmed) return "";

  const withoutLeading = trimmed.replace(/^\/+/, "");
  if (withoutLeading.startsWith("uploads/")) {
    return `/${withoutLeading}`;
  }
  if (trimmed.startsWith("/uploads/")) {
    return trimmed;
  }
  if (withoutLeading.startsWith("media/uploads/")) {
    return `/uploads/${withoutLeading.slice("media/uploads/".length)}`;
  }
  if (withoutLeading.startsWith("inventory/uploads/")) {
    return `/uploads/${withoutLeading.slice("inventory/uploads/".length)}`;
  }
  if (trimmed.startsWith(`${APP_MEDIA_PREFIX}/`)) {
    return `/uploads/${trimmed.slice(APP_MEDIA_PREFIX.length + 1)}`;
  }
  if (trimmed.startsWith(`${LEGACY_APP_MEDIA_PREFIX}/`)) {
    return `/uploads/${trimmed.slice(LEGACY_APP_MEDIA_PREFIX.length + 1)}`;
  }

  // bare filename
  if (!withoutLeading.includes("/")) {
    return `/uploads/${withoutLeading}`;
  }

  return "";
};

/**
 * App route path for copy/share: `/media/uploads/filename...`
 */
export const toAppMediaPath = (value) => {
  const uploadPath = toUploadRelativePath(value);
  if (!uploadPath) return "";
  const rest = uploadPath.replace(/^\/uploads\//, "");
  if (!rest || rest.includes("..")) return "";
  return `${APP_MEDIA_PREFIX}/${rest}`;
};

/**
 * Absolute app URL for clipboard.
 */
export const toAppMediaAbsoluteUrl = (value) => {
  const path = toAppMediaPath(value);
  if (!path) return "";
  if (typeof window === "undefined" || !window.location?.origin) return path;
  return `${window.location.origin}${path}`;
};

/**
 * API path for secure stream: `secure-uploads/filename...`
 */
export const toSecureUploadApiPath = (value) => {
  const uploadPath = toUploadRelativePath(value);
  if (!uploadPath) return "";
  const rest = uploadPath.replace(/^\/uploads\//, "");
  if (!rest || rest.includes("..")) return "";
  return `secure-uploads/${rest}`;
};

const pickMediaUrl = (item) => {
  if (!item || typeof item !== "object") return "";
  return item.media?.url || item.url || "";
};

const pickMediaId = (item) => {
  if (!item || typeof item !== "object") return null;

  const media = item.media;
  if (Array.isArray(media) && media[0]?.id) return media[0].id;
  if (media?.id) return media.id;
  if (item.id && item.url) return item.id;

  return null;
};

export const extractUploadedMediaPath = (payload) => {
  if (!payload) return "";

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const url = pickMediaUrl(item);
      if (url) return url;
    }
    return "";
  }

  if (Array.isArray(payload?.data)) {
    for (const item of payload.data) {
      const url = pickMediaUrl(item);
      if (url) return url;
    }
    return "";
  }

  return pickMediaUrl(payload);
};

export const extractUploadedMediaId = (payload) => {
  if (!payload) return null;

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const id = pickMediaId(item);
      if (id) return id;
    }
    return null;
  }

  if (Array.isArray(payload?.data)) {
    for (const item of payload.data) {
      const id = pickMediaId(item);
      if (id) return id;
    }
    return null;
  }

  return pickMediaId(payload);
};
